import {
  formatNumberToSpecialValues,
  formatSpecialValuesToNumber,
} from "../../utils";
import convert from "../convert-units/lib/convert_units";


const customUnitMap = {
  time: {
    baseUnit: "s",
    customUnits: [
      "h:min:s:ms",
      "h:min:s",
      "h:min",
      "min:s:ms",
      "min:s",
      "s:ms",
      "h:min:s:cs",
      "min:s:cs",
      "s:cs",
    ],
  },
  angle: {
    baseUnit: "°",
    customUnits: [`° ' "`, `' "`, `° '`],
  },
};

const relativeUnits = ["%", "ppm"];

const isCustomUnit = (unit) => {
  for (const [key, value] of Object.entries(customUnitMap)) {
    if (value.customUnits.includes(unit)) {
      return true;
    }
  }
  return false;
};

export const isCovertPossible = (from, to) => {
  if (from === "h:min:s:ms") {
    from = "ms";
  }

  let possibilities;
  try {
    possibilities = convert().from(from).possibilities();
    return possibilities?.includes(to) || false;
  } catch (e) {
    console.log(
      "[ERROR] Failed to check unit conversion possibility, error: ",
      e
    );
    return false;
  }
};

const getBaseUnitForCustomUnit = (unit) => {
  for (const [key, value] of Object.entries(customUnitMap)) {
    if (value.customUnits.includes(unit)) {
      return value.baseUnit;
    }
  }
  return null;
};

export const getBaseUnit = (unit) => {
  try {
    // return null for empty or null units
    if (!unit || unit.trim() === "") {
      return null;
    }

    // for relative units return null
    if (unit.includes("%") || unit.includes("ppm")) {
      return null;
    }

    // Check if the unit is a custom unit
    let baseUnit = getBaseUnitForCustomUnit(unit);
    if (baseUnit) {
      return baseUnit;
    }

    // Get the base unit for standard units
    const { measure, system } = convert().describe(unit);
    const measures = convert()._measures;
    const anchor = measures[measure]._anchors["metric"];
    return anchor.unit;
  } catch (e) {
    // console.error(`[ERROR] Failed to get base unit for ${unit}, error: `, e);
    return null;
  }
};

export const relativeUnitConvertor = (val, from, to, baseValue = null) => {
  if (!baseValue || (from?.includes("%") && to?.includes("%"))) {
    return val;
  }
  baseValue = String(baseValue || "").trim();
  let baseValueUnit = baseValue?.split("#")[1];
  baseValue = Number(baseValue?.split("#")[0]);

  let res = val;

  if (from?.trim() === "ppm") {
    res = (val / 1000000) * (baseValue || 1);
  } else if (to?.trim() === "ppm") {
    res = val * 1000000 * (baseValue || 1);
  } else if (from?.trim() === "%" && baseValue != null) {
    res = (Math.abs(Number(val)) * Math.abs(Number(baseValue))) / 100;
    if (baseValueUnit) {
      res = unitConvertor(res, baseValueUnit, to, null);
    }
  } else if (to?.trim() === "%" && baseValue != null) {
    res = unitConvertor(Math.abs(Number(val)), from, baseValueUnit, null);
    res = (res * 100) / Math.abs(Number(baseValue));
    if (baseValueUnit) {
    }
  } else {
    res = unitConvertor(val, from, to);
  }

  return res;
};

export default function unitConvertor(value, fromUnit, toUnit, baseValue) {
  if (String(value).includes("$")) {
    value = value.replaceAll("$", "");
  }
  let convertedValue = value;

  // Unary unit coversions
  // convert relative units to absolute units
  if (relativeUnits.includes(fromUnit) || relativeUnits.includes(toUnit)) {
    convertedValue = relativeUnitConvertor(value, fromUnit, toUnit, baseValue);
    return convertedValue;
  }

  // Binary unit coversions
  if (!fromUnit || !toUnit) {
    return convertedValue;
  }

  // custom to custom unit conversion
  if (isCustomUnit(fromUnit) && isCustomUnit(toUnit)) {
    // TODO: implement custom to custom unit conversion
  }

  // ignore cross measure unit conversion
  if (getBaseUnit(fromUnit) !== getBaseUnit(toUnit)) {
    return convertedValue;
  }

  // standard to custom unit conversion
  if (!isCustomUnit(fromUnit) && isCustomUnit(toUnit)) {
    if (fromUnit == "s") {
      value = convert(value).from(fromUnit.trim()).to("ms");
      fromUnit = "ms";
      convertedValue = formatNumberToSpecialValues(value, toUnit);
    } else if (fromUnit == "°") {
      value = convert(value).from(fromUnit.trim()).to("mas");
      fromUnit = "mas";
      convertedValue = formatNumberToSpecialValues(value, toUnit);
    }
    return convertedValue;
  }

  // process special values to numerical values, custom units to standard units
  if (isCustomUnit(fromUnit) && !isCustomUnit(toUnit)) {
    let specialValueUnit, currentSpecialValueUnit;
    [value, specialValueUnit, currentSpecialValueUnit] =
      formatSpecialValuesToNumber(value, fromUnit);
    if (currentSpecialValueUnit !== "number") {
      fromUnit = currentSpecialValueUnit;
    }
  }

  // standard unit conversion
  if (fromUnit === toUnit) return value;
  try {
    convertedValue = convert(value).from(fromUnit.trim()).to(toUnit.trim());
  } catch (e) {
    console.log(
      `[ERROR] Failed to convert unit (from: ${fromUnit}, to: ${toUnit}), error: `,
      e
    );
    return convertedValue;
  }

  return convertedValue;
}
